import React from 'react';
import { 
  FileText, Package, History, MapPin, Calendar, CreditCard, 
  Truck, Phone, Mail, CheckCircle, XCircle, Send
} from 'lucide-react';

const PurchaseOrderDetailTabs = ({ activeTab, po, supplier, paymentTerm, shippingMethod, poItems }) => {
  // Calculate receipt status
  const calculateReceiptStatus = (item) => {
    if (item.received_quantity === 0) {
      return { status: 'pending', label: '未收貨', color: '#F59E0B' };
    } else if (item.received_quantity < item.quantity) {
      return { status: 'partial', label: '部分收貨', color: '#8B5CF6' };
    } else {
      return { status: 'complete', label: '已收貨', color: '#10B981' };
    }
  };
  
  // Get receipts for this PO
  const poReceipts = poItems.filter(item => item.po_id === po.id);

  // Details Tab
  const renderDetailsTab = () => (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left Column - Order Info */}
      <div className="lg:col-span-2 space-y-6">
        {/* Order Items */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-medium">訂單項目</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider">
                <tr>
                  <th className="px-6 py-3 text-left">產品</th>
                  <th className="px-6 py-3 text-left">描述</th>
                  <th className="px-6 py-3 text-right">數量</th>
                  <th className="px-6 py-3 text-right">單價</th>
                  <th className="px-6 py-3 text-right">小計</th>
                  <th className="px-6 py-3 text-center">收貨狀態</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white text-sm">
                {poItems && poItems.length > 0 ? (
                  poItems.map(item => {
                    const receiptStatus = calculateReceiptStatus(item);
                    
                    return (
                      <tr key={item.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <p className="font-medium text-gray-900">{item.product_name}</p>
                            <p className="text-gray-500">{item.product_id}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">{item.product_name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          {(item.quantity || 0).toLocaleString()} 件
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          ${(item.unit_price || 0).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right font-medium">
                          ${(item.line_total || 0).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex justify-center">
                            <span 
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                              style={{
                                backgroundColor: receiptStatus.color + '20',
                                color: receiptStatus.color
                              }}
                            >
                              {receiptStatus.label}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                      此採購訂單沒有項目資料
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {/* Order Summary */}
          <div className="p-6 bg-gray-50 border-t border-gray-100">
            <div className="flex flex-col items-end space-y-2">
              <div className="flex justify-between">
                <span>小計:</span>
                <span className="font-medium">${(po.subtotal || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span>稅額:</span>
                <span className="font-medium">${(po.tax || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span>運費:</span>
                <span className="font-medium">${(po.shipping_cost || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between border-t border-gray-200 pt-2">
                <span className="font-bold">總計:</span>
                <span className="font-bold text-lg">${(po.total || po.total_amount || 0).toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Notes */}
        {po.notes && (
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <h2 className="text-lg font-medium mb-4">備註</h2>
            <p className="text-gray-700">{po.notes}</p>
          </div>
        )}
        
        {/* Attachments */}
        {po.attachments && po.attachments.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <h2 className="text-lg font-medium mb-4">附件</h2>
            <div className="space-y-3">
              {po.attachments.map(attachment => (
                <div key={attachment.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-blue-50 rounded-md">
                      <FileText className="h-5 w-5 text-blue-500" />
                    </div>
                    <div>
                      <p className="font-medium">{attachment.file_name}</p>
                      <p className="text-xs text-gray-500">
                        上傳於 {new Date(attachment.uploaded_at).toLocaleDateString()} 由 {attachment.uploaded_by}
                      </p>
                    </div>
                  </div>
                  <button className="text-blue-600 hover:text-blue-800">
                    下載
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Right Column - Supplier & Order Info */}
      <div className="space-y-6">
        {/* Supplier Info */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium">供應商資訊</h2>
            <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
              查看詳情
            </button>
          </div>
          
          <div className="space-y-4">
            <div>
              <p className="text-lg font-medium">{supplier.name}</p>
              {supplier.contact_person && (
                <p className="text-gray-500">{supplier.contact_person}</p>
              )}
            </div>
            
            <div className="space-y-2">
              {supplier.phone && (
                <div className="flex items-start space-x-3">
                  <div className="p-1">
                    <Phone className="h-4 w-4 text-gray-400" />
                  </div>
                  <span>{supplier.phone}</span>
                </div>
              )}
              
              {supplier.email && (
                <div className="flex items-start space-x-3">
                  <div className="p-1">
                    <Mail className="h-4 w-4 text-gray-400" />
                  </div>
                  <span>{supplier.email}</span>
                </div>
              )}
              
              {supplier.address && (
                <div className="flex items-start space-x-3">
                  <div className="p-1">
                    <MapPin className="h-4 w-4 text-gray-400" />
                  </div>
                  <span>{supplier.address}</span>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Order Details */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h2 className="text-lg font-medium mb-4">訂單資訊</h2>
          
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="p-1">
                <Calendar className="h-4 w-4 text-gray-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500">訂單日期</p>
                <p>{po.order_date ? new Date(po.order_date).toLocaleDateString() : '未知'}</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <div className="p-1">
                <Calendar className="h-4 w-4 text-gray-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500">預計交期</p>
                <p>{po.expected_delivery ? new Date(po.expected_delivery).toLocaleDateString() : '未知'}</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <div className="p-1">
                <CreditCard className="h-4 w-4 text-gray-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500">付款條件</p>
                <p>{paymentTerm || '未知'}</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <div className="p-1">
                <Truck className="h-4 w-4 text-gray-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500">運送方式</p>
                <p>{shippingMethod || '未知'}</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <div className="p-1">
                <MapPin className="h-4 w-4 text-gray-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500">運送地址</p>
                <p>{po.shipping_address}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Receipts Tab
  const renderReceiptsTab = () => (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
      <div className="p-6 border-b border-gray-100">
        <h2 className="text-lg font-medium">收貨記錄</h2>
      </div>
      
      {poReceipts.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider">
              <tr>
                <th className="px-6 py-3 text-left">收貨編號</th>
                <th className="px-6 py-3 text-left">收貨日期</th>
                <th className="px-6 py-3 text-left">收貨人</th>
                <th className="px-6 py-3 text-right">數量</th>
                <th className="px-6 py-3 text-center">品質檢查</th>
                <th className="px-6 py-3 text-left">儲位</th>
                <th className="px-6 py-3 text-left">備註</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white text-sm">
              {poReceipts.map(item => (
                <tr key={item.id}>
                  <td className="px-6 py-4 whitespace-nowrap font-medium">{item.receipt_number}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {new Date(item.receipt_date).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">{item.received_by}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">{item.quantity}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span 
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${item.quality_check === 'passed' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
                    >
                      {item.quality_check === 'passed' ? '通過' : '未通過'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">{item.location}</td>
                  <td className="px-6 py-4">{item.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="p-8 text-center text-gray-500">
          尚無收貨記錄
        </div>
      )}
    </div>
  );

  // History Tab
  const renderHistoryTab = () => (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
      <h2 className="text-lg font-medium mb-6">訂單歷程</h2>
      
      {po.history && po.history.length > 0 ? (
        <div className="space-y-6">
          {po.history.map((event, index) => {
            // Icon based on action
            let EventIcon;
            let iconColor;
            
            switch (event.action) {
              case 'created':
                EventIcon = FileText;
                iconColor = '#3B82F6';
                break;
              case 'approved':
                EventIcon = CheckCircle;
                iconColor = '#10B981';
                break;
              case 'sent':
                EventIcon = Send;
                iconColor = '#6366F1';
                break;
              case 'partial_receipt':
              case 'received':
                EventIcon = Package;
                iconColor = '#8B5CF6';
                break;
              case 'cancelled':
                EventIcon = XCircle;
                iconColor = '#EF4444';
                break;
              default:
                EventIcon = History;
                iconColor = '#6B7280';
            }
            
            return (
              <div key={event.id} className="flex">
                <div className="mr-4 flex flex-col items-center">
                  <div 
                    className="p-2 rounded-full"
                    style={{ backgroundColor: iconColor + '20' }}
                  >
                    <EventIcon className="h-5 w-5" style={{ color: iconColor }} />
                  </div>
                  {index < po.history.length - 1 && (
                    <div className="w-px bg-gray-200 h-full flex-grow mt-2"></div>
                  )}
                </div>
                <div className="pb-6">
                  <p className="font-medium">
                    {event.action === 'created' && '創建訂單'}
                    {event.action === 'approved' && '核准訂單'}
                    {event.action === 'sent' && '發送訂單'}
                    {event.action === 'partial_receipt' && '部分收貨'}
                    {event.action === 'received' && '完全收貨'}
                    {event.action === 'cancelled' && '取消訂單'}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    {event.action_at ? new Date(event.action_at).toLocaleString() : '未知時間'} 由 {event.action_by || '未知'}
                  </p>
                  {event.details && (
                    <p className="mt-2 text-gray-700">{event.details}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="p-8 text-center text-gray-500">
          尚無訂單歷程記錄
        </div>
      )}
    </div>
  );

  // Render the active tab
  switch (activeTab) {
    case 'details':
      return renderDetailsTab();
    case 'receipts':
      return renderReceiptsTab();
    case 'history':
      return renderHistoryTab();
    default:
      return renderDetailsTab();
  }
};

export default PurchaseOrderDetailTabs;
